<script lang="ts">
  // SVG time-series chart. Fixed Y-axis column. Zoom changes the visible TIME
  // WINDOW (narrows / broadens the duration) — the chart never scrolls or scales
  // as an image. Hover a point for its time + value. Optional hard y-caps
  // (yMin/yMax) and reference band/thresholds.
  import { parseRange, statusOf } from '../lib/labViz';

  type P = { t: number; y: number; flag?: string };
  type Series = { label?: string; color: string; refRange?: string; points: P[] };
  let {
    points = [], refRange = '', color = 'var(--accent)', label = '', unit = '',
    series, t0, t1, height = 132, interactive = false, yMin, yMax, timeAxis = false, showXLabels = true, yZoomable = false,
    zoom = $bindable(1), centerMs = $bindable(0), gapMode = 'fixed',
  }: {
    points?: P[]; refRange?: string; color?: string; label?: string; unit?: string;
    series?: Series[]; t0: number; t1: number; height?: number; interactive?: boolean; yMin?: number; yMax?: number; timeAxis?: boolean; showXLabels?: boolean; yZoomable?: boolean;
    // zoom + centerMs are $bindable so several charts can share one window (synced zoom/pan).
    zoom?: number; centerMs?: number; gapMode?: 'fixed' | 'adaptive' | 'never';
  } = $props();

  const YAXIS_W = 50, PADL = 6, PADR = 14, PADT = 16;
  const PADB = $derived(timeAxis ? 40 : 28);
  const H = $derived(height);
  const DAY = 86400000;
  const startOfDayMs = (ts: number) => { const d = new Date(ts); return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime(); };
  let plotEl = $state<HTMLDivElement>();
  let cw = $state(0);
  const plotW = $derived(Math.max(cw || 320, 120));
  const clipId = `tc-${Math.random().toString(36).slice(2, 9)}`;

  const lines = $derived<Series[]>(series && series.length ? series : [{ label, color, refRange, points }]);
  const single = $derived(lines.length === 1);
  const clean = (ps: P[]) => ps.filter((p) => Number.isFinite(p.y) && Number.isFinite(p.t)).sort((a, b) => a.t - b.t);

  // ── Visible time window (zoom = narrower window) ────────────────────────────────
  let _rangeKey = '';
  $effect(() => { const k = `${t0}|${t1}`; if (k === _rangeKey) return; _rangeKey = k; zoom = 1; centerMs = (t0 + t1) / 2; });   // reset window when range changes
  const full = $derived(Math.max(1, t1 - t0));
  const viewSpan = $derived(full / zoom);
  const vt0 = $derived(Math.min(Math.max(centerMs - viewSpan / 2, t0), t1 - viewSpan));
  const vt1 = $derived(vt0 + viewSpan);

  const yBase = $derived.by(() => {
    // Autoscale to the VISIBLE window: when zoomed/panned, fit the y-axis to the points
    // currently in view (vt0..vt1) so a zoomed region fills the height. At full view the
    // window is the whole range, so this matches "fit all". Reference band is included
    // only at full view (so it doesn't compress a zoomed-in region of high values).
    let lo = Infinity, hi = -Infinity;
    for (const l of lines) for (const p of l.points) {
      if (Number.isFinite(p.y) && p.t >= vt0 && p.t <= vt1) { lo = Math.min(lo, p.y); hi = Math.max(hi, p.y); }
    }
    if (!Number.isFinite(lo)) {   // no points in view → fall back to all points so the axis still scales
      for (const l of lines) for (const p of l.points) if (Number.isFinite(p.y)) { lo = Math.min(lo, p.y); hi = Math.max(hi, p.y); }
    }
    if (zoom <= 1) for (const l of lines) { const r = parseRange(l.refRange || ''); if (r) { lo = Math.min(lo, r.min); hi = Math.max(hi, r.max); } }
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) { lo = 0; hi = 1; }
    if (lo === hi) { lo -= 1; hi += 1; }
    const pad = (hi - lo) * 0.14;
    lo = yMin != null ? yMin : lo - pad;
    hi = yMax != null ? yMax : hi + pad;
    return { lo, hi };
  });

  // ── Optional y-axis zoom: a side control spins to narrow the value range ──────────
  // yBase is the auto/clamped domain; yzoom narrows it around ycenter (drag to pan).
  const MAX_YZOOM = 25;
  let yzoom = $state(1), ycenter = $state(0), _yKey = '';
  $effect(() => {
    if (!yZoomable) return;
    const k = `${yBase.lo}|${yBase.hi}`; if (k === _yKey) return; _yKey = k;
    yzoom = 1; ycenter = (yBase.lo + yBase.hi) / 2;
  });
  const yDomain = $derived.by(() => {
    if (!yZoomable || yzoom <= 1) return yBase;
    const span = (yBase.hi - yBase.lo) / yzoom;
    const lo = Math.min(Math.max(ycenter - span / 2, yBase.lo), yBase.hi - span);
    return { lo, hi: lo + span };
  });
  // Keep the zoom window's centre within the base range (no pan past the edges).
  const clampY = (c: number) => { const span = (yBase.hi - yBase.lo) / Math.max(1, yzoom); return Math.min(Math.max(c, yBase.lo + span / 2), yBase.hi - span / 2); };
  function yZoomBy(deltaY: number) {
    const f = deltaY < 0 ? 1.12 : 1 / 1.12;                     // spin up → zoom in (reduce range)
    yzoom = Math.min(MAX_YZOOM, Math.max(1, yzoom * f));
    ycenter = clampY(ycenter);
  }
  function yPanBy(deltaY: number) {                             // scroll up → higher values
    const span = (yBase.hi - yBase.lo) / yzoom;
    ycenter = clampY(ycenter + (deltaY < 0 ? 1 : -1) * span * 0.15);
  }
  function yWheelAction(node: HTMLElement) {
    const h = (e: WheelEvent) => { e.preventDefault(); e.stopPropagation(); yZoomBy(e.deltaY); };
    node.addEventListener('wheel', h, { passive: false });
    return { destroy: () => node.removeEventListener('wheel', h) };
  }
  function yPanWheelAction(node: HTMLElement) {
    const h = (e: WheelEvent) => { e.preventDefault(); e.stopPropagation(); yPanBy(e.deltaY); };
    node.addEventListener('wheel', h, { passive: false });
    return { destroy: () => node.removeEventListener('wheel', h) };
  }
  let yDrag = false, yDnY = 0, yDnCenter = 0;
  function yDown(e: PointerEvent) { e.stopPropagation(); yDrag = true; yDnY = e.clientY; yDnCenter = ycenter; (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); }
  function yMove(e: PointerEvent) {
    if (!yDrag) return;
    const plotH = Math.max(1, H - PADT - PADB), span = (yBase.hi - yBase.lo) / yzoom;
    ycenter = clampY(yDnCenter - ((e.clientY - yDnY) / plotH) * span);   // drag down → lower values (screen y is inverted)
  }
  function yUp() { yDrag = false; }
  function yReset() { yzoom = 1; ycenter = (yBase.lo + yBase.hi) / 2; }
  function onYKey(e: KeyboardEvent) {
    if (e.key === 'ArrowUp') { e.preventDefault(); yZoomBy(-1); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); yZoomBy(1); }
    else if (e.key === 'Home' || e.key === 'Escape') yReset();
  }
  function onPanKey(e: KeyboardEvent) {
    if (e.key === 'ArrowUp') { e.preventDefault(); yPanBy(-1); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); yPanBy(1); }
    else if (e.key === 'Home' || e.key === 'Escape') yReset();
  }

  const innerW = $derived(plotW - PADL - PADR);
  const X = (t: number) => PADL + innerW * ((t - vt0) / Math.max(1, vt1 - vt0));
  const Y = (y: number) => { const { lo, hi } = yDomain; return PADT + (H - PADT - PADB) * (1 - (y - lo) / (hi - lo)); };
  const GAP_MS = 14 * 86400000;   // connect across breaks up to 2 weeks; longer gaps split the line
  // Break the line where a gap is much larger than the typical spacing, so sparse /
  // non-uniform data isn't misleadingly connected across long empty stretches.
  function gapThreshold(c: P[]): number {
    if (gapMode === 'never') return Infinity;                 // always connect every marker
    if (gapMode !== 'adaptive') return GAP_MS;
    if (c.length < 3) return Infinity;                       // too few points to judge → keep connected
    const gaps: number[] = [];
    for (let i = 1; i < c.length; i++) gaps.push(c[i].t - c[i - 1].t);
    gaps.sort((a, b) => a - b);
    const med = gaps[Math.floor(gaps.length / 2)] || DAY;
    return Math.max(med * 4, GAP_MS);                        // break where a gap > 4× the median, but always connect up to 2 weeks
  }
  const pathOf = (ps: P[]) => {
    const c = clean(ps); const gap = gapThreshold(c); let d = '';
    for (let i = 0; i < c.length; i++) { const cmd = i === 0 || c[i].t - c[i - 1].t > gap ? 'M' : 'L'; d += `${cmd}${X(c[i].t).toFixed(1)} ${Y(c[i].y).toFixed(1)} `; }
    return d.trim();
  };
  const band = $derived(single ? parseRange(lines[0].refRange || '') : null);
  const dotColor = (l: Series, p: P) => {
    if (!single) return l.color;
    const s = statusOf(p.y, parseRange(l.refRange || ''), p.flag);
    return s === 'high' ? 'var(--warn)' : s === 'low' ? 'var(--info)' : s === 'normal' ? 'var(--ok)' : 'var(--text-3)';
  };
  const spanDays = $derived((vt1 - vt0) / 86400000);
  const ticks = $derived.by(() => {
    const n = Math.max(2, Math.min(40, Math.floor(innerW / 78)));
    return Array.from({ length: n }, (_, i) => vt0 + (vt1 - vt0) * (i / (n - 1)));
  });
  const tickLabel = (t: number) => {
    const d = new Date(t);
    if (spanDays > 120) return `${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;   // long span → month/year
    const base = `${d.getMonth() + 1}/${d.getDate()}`;
    return spanDays <= 2 ? `${base} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}` : base;
  };
  const fmtV = (v: number) => (Math.abs(v) >= 100 ? Math.round(v) : Math.round(v * 10) / 10);

  // Two-tier date + hour-of-day axis (vitals): a date row with per-day separators,
  // and hour ticks (0–24) within each day. Falls back to date ticks when days are
  // too narrow to fit hours.
  const dayWidthPx = $derived(innerW / Math.max(1e-6, (vt1 - vt0) / DAY));
  const showHours = $derived(timeAxis && dayWidthPx >= 64);
  const days = $derived.by(() => { if (!timeAxis) return [] as number[]; const out: number[] = []; for (let d = startOfDayMs(vt0); d <= vt1; d += DAY) out.push(d); return out; });
  const hourStep = $derived(dayWidthPx >= 220 ? 2 : dayWidthPx >= 130 ? 3 : 6);
  const hourTicks = $derived.by(() => {
    if (!showHours) return [] as { t: number; h: number }[];
    const out: { t: number; h: number }[] = [];
    for (const day of days) for (let h = 0; h < 24; h += hourStep) { const t = day + h * 3600000; if (t >= vt0 && t <= vt1) out.push({ t, h }); }
    return out;
  });
  const md = (ms: number) => { const d = new Date(ms); return `${d.getMonth() + 1}/${d.getDate()}`; };

  // ── Calendar axis (long spans): month minor + year major gridlines; year labels
  //    with month labels stepped by density so they never crowd. ────────────────────
  const startOfMonthMs = (ts: number) => { const d = new Date(ts); return new Date(d.getFullYear(), d.getMonth(), 1).getTime(); };
  const nextMonthMs = (ts: number) => { const d = new Date(ts); return new Date(d.getFullYear(), d.getMonth() + 1, 1).getTime(); };
  const nextYearMs = (ts: number) => new Date(new Date(ts).getFullYear() + 1, 0, 1).getTime();
  const showCalendar = $derived(timeAxis && !showHours && spanDays > 120);
  const monthWidthPx = $derived(showCalendar ? innerW / Math.max(1, (vt1 - vt0) / (30.44 * DAY)) : 0);
  const showMonthGrid = $derived(showCalendar && monthWidthPx >= 9);            // hide month minors once they'd crowd (very long spans)
  const months = $derived.by(() => {
    if (!showCalendar) return [] as number[];
    const out: number[] = []; let m = startOfMonthMs(vt0); if (m < vt0) m = nextMonthMs(m);
    while (m <= vt1) { out.push(m); m = nextMonthMs(m); }
    return out;
  });
  const years = $derived.by(() => {
    if (!showCalendar) return [] as number[];
    const out: number[] = []; let y = new Date(new Date(vt0).getFullYear(), 0, 1).getTime(); if (y < vt0) y = nextYearMs(y);
    while (y <= vt1) { out.push(y); y = nextYearMs(y); }
    return out;
  });
  // Labels: step months at 3 / 6 / 12-month intervals by density (≈ every 3rd month
  // on a multi-year view, semiannual on ~5yr, annual on ~10yr). Show the YEAR at each
  // January and the plain month number in between, so labels stay narrow and uncluttered.
  // Steps extend to 2 / 5 / 10 years so a decade-long record on a narrow chart still
  // gets one label per ≥50px instead of a year label jammed into every January.
  const labelStep = $derived([3, 6, 12, 24, 60, 120].find((s) => s * monthWidthPx >= 50) ?? 120);
  const calLabeled = (ms: number) => { const d = new Date(ms); return (d.getFullYear() * 12 + d.getMonth()) % labelStep === 0; };
  const calIsYear = (ms: number) => new Date(ms).getMonth() === 0;
  const calText = (ms: number) => { const d = new Date(ms); return d.getMonth() === 0 ? String(d.getFullYear()) : String(d.getMonth() + 1); };

  function niceTicks(lo: number, hi: number, n: number): number[] {
    const span = hi - lo; if (!(span > 0)) return [lo];
    const step0 = span / n, mag = Math.pow(10, Math.floor(Math.log10(step0))), norm = step0 / mag;
    // Round the raw step to the NEAREST nice number (1/2/5/10), not down — otherwise a
    // raw step of 4.8 collapses to 2 and floods the axis with twice as many ticks.
    const step = (norm < 1.5 ? 1 : norm < 3 ? 2 : norm < 7 ? 5 : 10) * mag;
    const out: number[] = [];
    for (let v = Math.ceil(lo / step) * step; v <= hi + step * 0.001; v += step) out.push(Math.round(v * 1000) / 1000);
    return out;
  }
  const yTicks = $derived(niceTicks(yDomain.lo, yDomain.hi, Math.max(3, Math.min(6, Math.floor((H - PADT - PADB) / 30)))));
  const latest = $derived(single ? clean(lines[0].points).at(-1) : undefined);

  // ── Zoom / pan (time window) ────────────────────────────────────────────────────
  const maxZoom = $derived(Math.max(1, full / 3600000));   // down to ~1 hour
  const fmtDT = (t: number) => { const d = new Date(t); return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(2)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; };
  const zoomIn = () => (zoom = Math.min(maxZoom, zoom * 1.6));
  const zoomOut = () => (zoom = Math.max(1, zoom / 1.6));
  const zoomReset = () => { zoom = 1; centerMs = (t0 + t1) / 2; };
  function onWheel(e: WheelEvent) {
    if (!interactive) return;
    // Horizontal scroll pans the time axis; vertical scroll is left to the page.
    // Ctrl/⌘+wheel still zooms (standard convention) for mouse users without a
    // horizontal wheel. Zoom buttons remain the primary zoom control.
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const rect = plotEl!.getBoundingClientRect();
      const frac = Math.min(1, Math.max(0, (e.clientX - rect.left - PADL) / Math.max(1, innerW)));
      const tUnder = vt0 + frac * (vt1 - vt0);
      const nz = Math.min(maxZoom, Math.max(1, zoom * (e.deltaY < 0 ? 1.25 : 1 / 1.25)));
      centerMs = tUnder - (frac - 0.5) * (full / nz);
      zoom = nz;
      return;
    }
    // Pan when the gesture is horizontal: a trackpad's sideways swipe (deltaX) or,
    // for a plain mouse wheel (no deltaX), Shift+wheel — the standard convention.
    // Otherwise do nothing and let the page scroll normally.
    if (zoom <= 1) return;   // full range → nothing to pan
    const dx = e.shiftKey ? (e.deltaX || e.deltaY) : e.deltaX;
    if (Math.abs(dx) <= Math.abs(e.deltaY) && !e.shiftKey) return;   // vertical → page scrolls
    if (!dx) return;
    e.preventDefault();
    centerMs = centerMs + (dx / Math.max(1, innerW)) * (vt1 - vt0);
  }
  function wheelAction(node: HTMLDivElement) {
    const h = (e: WheelEvent) => onWheel(e);
    node.addEventListener('wheel', h, { passive: false });
    return { destroy: () => node.removeEventListener('wheel', h) };
  }
  let dragging = false, dnX = 0, dnCenter = 0;
  function onDown(e: PointerEvent) { if (!interactive || zoom <= 1) return; dragging = true; dnX = e.clientX; dnCenter = centerMs; plotEl!.setPointerCapture(e.pointerId); }
  function onMove(e: PointerEvent) { if (dragging) centerMs = dnCenter - ((e.clientX - dnX) / Math.max(1, innerW)) * (vt1 - vt0); }
  function onUp() { dragging = false; }

  // ── Hover tooltip ───────────────────────────────────────────────────────────────
  let hover = $state<{ left: number; top: number; text: string } | null>(null);
  function showHover(p: P) {
    hover = { left: YAXIS_W + X(p.t), top: Y(p.y), text: `${fmtV(p.y)}${unit ? ' ' + unit : ''} · ${fmtDT(p.t)}` };
  }
</script>

<div class="chart">
  {#if single || interactive}
    <div class="ttl">
      <span class="lbl">{label}</span>
      {#if unit}<span class="uctr">{unit}</span>{/if}
      <span class="right">
        {#if latest}<span class="last num" style={`color:${dotColor(lines[0], latest)}`}>{fmtV(latest.y)}</span>{/if}
        {#if interactive}
          <span class="zoom">
            <button onclick={zoomOut} aria-label="Broaden time range" title="Zoom out (longer span)">−</button>
            <button onclick={zoomReset} aria-label="Full range" title="Full range">⤢</button>
            <button onclick={zoomIn} aria-label="Narrow time range" title="Zoom in (shorter span)">+</button>
          </span>
        {/if}
      </span>
    </div>
  {/if}

  <div class="plotrow">
    <svg class="yaxis" width={YAXIS_W} height={H} viewBox={`0 0 ${YAXIS_W} ${H}`} aria-hidden="true">
      {#each yTicks as yt (yt)}<text class="ax yax" x={YAXIS_W - 6} y={Y(yt) + 3.5} text-anchor="end">{fmtV(yt)}</text>{/each}
    </svg>

    <div class="plotwrap" class:interactive class:grab={interactive && zoom > 1} bind:this={plotEl} bind:clientWidth={cw}
      use:wheelAction onpointerdown={onDown} onpointermove={onMove} onpointerup={onUp} onpointercancel={onUp}
      role="group" aria-label={`${label || 'series'} — Shift+scroll (or swipe sideways) to pan the time range; ⌘/Ctrl+scroll to zoom`}>
      <svg width={plotW} height={H} viewBox={`0 0 ${plotW} ${H}`} role="img" aria-label={`${label || 'series'} over time`}>
        <clipPath id={clipId}><rect x={PADL} y="0" width={Math.max(0, innerW)} height={H} /></clipPath>
        {#if band}
          <rect class="band" x={PADL} y={Y(band.max)} width={innerW} height={Math.max(0, Y(band.min) - Y(band.max))} />
          <line class="thr" x1={PADL} y1={Y(band.max)} x2={plotW - PADR} y2={Y(band.max)} />
          <line class="thr" x1={PADL} y1={Y(band.min)} x2={plotW - PADR} y2={Y(band.min)} />
        {/if}
        {#each yTicks as yt (yt)}<line class="ygrid" x1={PADL} y1={Y(yt)} x2={plotW - PADR} y2={Y(yt)} />{/each}
        <line class="base" x1={PADL} y1={H - PADB} x2={plotW - PADR} y2={H - PADB} />
        {#if showHours}
          {#each days as day (day)}
            {#if day > vt0}<line class="daysep" x1={X(day)} y1={PADT} x2={X(day)} y2={H - PADB} />{/if}
            {#if showXLabels}
              {@const lx = X(Math.max(day, vt0))}
              {@const rx = X(Math.min(day + DAY, vt1))}
              <!-- Center the date over the day's VISIBLE span; skip slivers (e.g. the
                   clamped leftmost partial day) too narrow to hold the label. -->
              {#if rx - lx >= 34}<text class="ax datelbl" x={(lx + rx) / 2} y={H - PADB + 15} text-anchor="middle">{md(day)}</text>{/if}
            {/if}
          {/each}
          {#each hourTicks as ht (ht.t)}
            <line class="tick" x1={X(ht.t)} y1={H - PADB} x2={X(ht.t)} y2={H - PADB + 4} />
            {#if showXLabels}<text class="ax hour" x={X(ht.t)} y={H - PADB + 28} text-anchor="middle">{ht.h}</text>{/if}
          {/each}
        {:else if showCalendar}
          <!-- Long span: months = minor gridlines, years = major gridlines, MM/YYYY labels. -->
          {#if showMonthGrid}
            {#each months as m (m)}{#if m > vt0}<line class="monthsep" x1={X(m)} y1={PADT} x2={X(m)} y2={H - PADB} />{/if}{/each}
          {/if}
          {#each years as yr (yr)}{#if yr > vt0}<line class="yearsep" x1={X(yr)} y1={PADT} x2={X(yr)} y2={H - PADB} />{/if}{/each}
          {#if showXLabels}
            {#each months as m (m)}
              {#if calLabeled(m)}
                <line class="tick" x1={X(m)} y1={H - PADB} x2={X(m)} y2={H - PADB + 4} />
                <text class="ax" class:datelbl={calIsYear(m)} x={X(m)} y={H - PADB + 15} text-anchor="middle">{calText(m)}</text>
              {/if}
            {/each}
          {/if}
        {:else}
          {#each ticks as tk (tk)}
            <line class="tick" x1={X(tk)} y1={H - PADB} x2={X(tk)} y2={H - PADB + 4} />
            {#if showXLabels}<text class="ax" x={X(tk)} y={H - PADB + 15} text-anchor="middle">{tickLabel(tk)}</text>{/if}
          {/each}
        {/if}
        <g clip-path={`url(#${clipId})`}>
          {#each lines as l (l.label ?? l.color)}
            {@const cps = clean(l.points)}
            {#if cps.length > 1}<path d={pathOf(l.points)} fill="none" style={`stroke:${l.color}`} stroke-width="1.75" vector-effect="non-scaling-stroke" stroke-linejoin="round" stroke-linecap="round" />{/if}
            {#each cps as p, pi (pi)}<circle cx={X(p.t)} cy={Y(p.y)} r="3" style={`fill:${dotColor(l, p)}`} role="img" aria-label={`${fmtV(p.y)} ${unit}`} onpointerenter={() => showHover(p)} onpointerleave={() => (hover = null)} />{/each}
          {/each}
        </g>
      </svg>
      {#if hover}<div class="tip" style={`left:${hover.left}px; top:${hover.top}px`}>{hover.text}</div>{/if}
      {#if yZoomable}
        <div class="yctrls">
          {#if yzoom > 1}
            <div class="yctl ypan"
              use:yPanWheelAction onpointerdown={yDown} onpointermove={yMove} onpointerup={yUp} onpointercancel={yUp}
              ondblclick={yReset} onkeydown={onPanKey} tabindex="0"
              role="button" aria-label="Pan the value axis vertically — scroll or drag up and down"
              title="Scroll to pan up/down · drag to move">
              <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 2.5 5.5 5M8 2.5 10.5 5M8 13.5 5.5 11M8 13.5 10.5 11M8 3v10" /></svg>
            </div>
          {/if}
          <div class="yctl yzoom" class:on={yzoom > 1}
            use:yWheelAction onpointerdown={yDown} onpointermove={yMove} onpointerup={yUp} onpointercancel={yUp}
            ondblclick={yReset} onkeydown={onYKey} tabindex="0"
            role="slider" aria-label="Zoom the value axis — scroll to narrow the range, drag to pan, double-click to reset"
            aria-valuemin={1} aria-valuemax={MAX_YZOOM} aria-valuenow={Math.round(yzoom * 10) / 10}
            title="Scroll to zoom the value axis · drag to pan · double-click to reset">
            <span class="ygrip" aria-hidden="true"></span>
            {#if yzoom > 1}<span class="yz-badge num" aria-hidden="true">{Math.round(yzoom * 10) / 10}×</span>{/if}
          </div>
        </div>
      {/if}
    </div>
  </div>
</div>

<style>
  .chart { position: relative; background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius-md); padding: var(--space-3); }
  .ttl { display: grid; grid-template-columns: 1fr auto 1fr; align-items: baseline; margin-bottom: 4px; gap: var(--space-2); }
  .lbl { justify-self: start; font-size: var(--fs-xs); font-weight: 700; letter-spacing: 0.02em; color: var(--text-2); }
  .uctr { justify-self: center; font-size: var(--fs-2xs); font-weight: 700; letter-spacing: 0.02em; color: var(--accent); }
  .right { justify-self: end; display: inline-flex; align-items: center; gap: var(--space-3); }
  .last { font-size: var(--fs-md); font-weight: 700; }
  .zoom { display: inline-flex; gap: 2px; }
  .zoom button { width: 22px; height: 22px; display: grid; place-items: center; border-radius: var(--radius-sm); border: 1px solid var(--line); color: var(--text-2); font-size: var(--fs-sm); font-weight: 700; line-height: 1; }
  .zoom button:hover { color: var(--text); background: var(--surface-2); border-color: var(--line-strong); }

  .plotrow { position: relative; display: grid; grid-template-columns: auto 1fr; align-items: start; }
  .yaxis { display: block; flex: 0 0 auto; }
  .plotwrap { position: relative; min-width: 0; touch-action: pan-y; }
  /* Value-axis controls — tucked on the left edge. The zoom grip spins (scroll) to
     narrow the range; once zoomed, a pan control appears above it to scroll/drag the
     window up & down. Both also drag; double-click resets. */
  .yctrls { position: absolute; left: 1px; top: 50%; transform: translateY(-50%); z-index: 4; display: flex; flex-direction: column; align-items: center; gap: 5px; }
  .yctl { display: flex; flex-direction: column; align-items: center; justify-content: center; width: 15px; border-radius: var(--radius-pill); background: color-mix(in srgb, var(--surface-2) 80%, transparent); border: 1px solid var(--line); color: var(--text-3); cursor: ns-resize; opacity: 0.5; transition: opacity var(--ease), color var(--ease), border-color var(--ease); touch-action: none; }
  .yctl:hover, .yctl:focus-visible { opacity: 1; color: var(--text); border-color: var(--line-strong); }
  .yctl:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
  .yzoom { gap: 3px; height: 66px; }
  .yzoom.on { opacity: 1; color: var(--accent); border-color: var(--accent); }
  .ypan { height: 32px; }
  .ypan svg { width: 12px; height: 12px; fill: none; stroke: currentColor; stroke-width: 1.5; stroke-linecap: round; stroke-linejoin: round; }
  .ygrip { width: 4px; height: 24px; border-radius: 2px; background: repeating-linear-gradient(to bottom, currentColor 0 2px, transparent 2px 4px); }
  .yz-badge { font-size: 8px; font-weight: 800; line-height: 1; letter-spacing: -0.02em; }
  .plotwrap.grab { cursor: grab; }
  .plotwrap.grab:active { cursor: grabbing; }
  svg { display: block; }
  .band { fill: var(--ok-soft); }
  .base { stroke: var(--line-strong); stroke-width: 1; }
  .tick { stroke: var(--line); stroke-width: 1; }
  .ygrid { stroke: var(--line); stroke-width: 1; opacity: 0.5; }
  .thr { stroke: var(--ok); stroke-width: 1; stroke-dasharray: 4 3; opacity: 0.6; }
  .daysep { stroke: var(--line-strong); stroke-width: 1; stroke-dasharray: 2 3; opacity: 0.55; }
  .monthsep { stroke: var(--line); stroke-width: 1; opacity: 0.3; }
  .yearsep { stroke: var(--line-strong); stroke-width: 1; opacity: 0.7; }
  .ax { fill: var(--text-3); font-size: 12px; font-weight: 600; }
  .ax.yax { fill: var(--text-2); }
  .ax.datelbl { fill: var(--text-2); font-weight: 800; }
  .ax.hour { fill: var(--text-3); font-size: 10px; }
  circle { cursor: pointer; }
  .tip { position: absolute; z-index: 5; transform: translate(-50%, calc(-100% - 8px)); padding: 3px 8px; background: var(--bg); border: 1px solid var(--line-strong); border-radius: var(--radius-sm); font-size: var(--fs-2xs); font-weight: 700; color: var(--text); white-space: nowrap; pointer-events: none; box-shadow: var(--shadow-sm); }
</style>
